<template>
  <div>
    <salvar-agendamento-envio-mensagem-dialog
      :is-dialog-open.sync="isEditarItemDialogOpen"
      :agendamento-mensagem-data.sync="agendamentoEmMassaSelecionado"
      @refetch-data="$emit('refetch-data')"
    />

    <v-data-table
      :headers="headers"
      :items="items"
      :footer-props="{
        'items-per-page-text':'Linhas por página',
        'page-text': '{0}-{1} de {2}',
        'items-per-page-all-text': 'Todos',
      }"
      :loading="loading"
      loading-text="Carregando itens"
      class="elevation-1"
      no-data-text="Nenhum agendamento de mensagem adicionado"
    >
      <template #[`item.nome`]="{ item }">
        <div class="d-flex align-center">
          <v-avatar
            :color="`${$vuetify.theme.dark ? 'primary' : '#DCDCDC'}`"
            size="32"
          >
            <span :class="`${$vuetify.theme.dark ? 'white--text' : 'primary--text'} font-weight-bold`">
              {{ item.nome.toUpperCase().charAt(0) }}{{ item.nome.toUpperCase().charAt(1) }}
            </span>
          </v-avatar>

          <div class="ms-3 text-capitalize">
            {{ item.nome }}
          </div>
        </div>
      </template>

      <template #[`item.dataCadastro`]="{ item }">
        <span>{{ functions.convertToLocaleDateTimeString(item.dataCadastro) }}</span>
      </template>

      <template #[`item.dataHoraEnvio`]="{ item }">
        <span>{{ functions.convertToLocaleDateTimeString(item.dataHoraEnvio) }}</span>
      </template>

      <template #[`item.canalTipoId`]="{ item }">
        <div class="d-flex justify-center">
          <v-icon class="mr-4">
            {{ functions.getIconFromCanalTipoId(item.canalTipoId) }}
          </v-icon>
        </div>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-menu
          v-if="podeMostrarBotaoEditar(item) || podeMostrarBotaoDetalhesDoEnvio(item)"
          left
          bottom
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              class="ml-n5"
              v-bind="attrs"
              v-on="on"
              color="primary"
            >
              <v-icon>
                {{ icons.mdiDotsVertical }}
              </v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item v-if="podeMostrarBotaoEditar(item)">
              <v-list-item-title
                role="button"
                @click="openEditarItemDialog(item)"
              >
                <v-icon
                  size="20"
                  class="mr-2"
                >
                  {{ icons.mdiFileEdit }}
                </v-icon>

                <span>Editar</span>
              </v-list-item-title>
            </v-list-item>

            <v-list-item v-if="podeMostrarBotaoDetalhesDoEnvio(item)">
              <v-list-item-title
                role="button"
                class="d-flex"
                @click="openItemDetalhesDialog(item)"
              >
                <v-icon
                  size="20"
                  class="mr-2"
                >
                  {{ icons.mdiClockOutline }}
                </v-icon>

                <span>Detalhes do envio</span>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import { mdiClockOutline, mdiDotsVertical, mdiEmail, mdiFileEdit, mdiMessageProcessing, mdiWhatsapp } from '@mdi/js'
import SalvarAgendamentoEnvioMensagemDialog from '@/views/envio-em-massa/agendamento-envio-mensagem/SalvarAgendamentoEnvioMensagemDialog.vue'
import AgendamentoEnvioMensagemStatusEnum from '@/models/entities/agendamento-envio-mensagem/AgendamentoEnvioMensagemStatusEnum'
import TiposDeCanaisEnum from '@/models/entities/canal/TiposDeCanaisEnum'
import { useRouter } from '@/@core/utils'

export default {
  components: {
    SalvarAgendamentoEnvioMensagemDialog,
  },

  props: {
    items: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },

  setup() {
    const headers = [
      { text: 'Nome agendamento', value: 'nome' },
      { text: 'Responsável', value: 'responsavelNome' },
      { text: 'Criado em', value: 'dataCadastro' },
      { text: 'Agendado para', value: 'dataHoraEnvio' },
      { text: 'Status envio', value: 'statusNome' },
      { text: 'Tipo de canal', value: 'canalTipoId', align: 'center' },
      {
        text: 'Opções',
        value: 'actions',
        align: 'center',
      },
    ]

    const isEditarItemDialogOpen = ref(false)
    const agendamentoEmMassaSelecionado = ref(null)

    const { router } = useRouter()

    function openEditarItemDialog(item) {
      isEditarItemDialogOpen.value = true
      agendamentoEmMassaSelecionado.value = JSON.parse(JSON.stringify(item))
    }

    function openItemDetalhesDialog(item) {
      router.push({ name: 'relatorio-envio-em-massa', query: { agendamentoId: item.id } })
    }

    function podeMostrarBotaoEditar(item) {
      return [AgendamentoEnvioMensagemStatusEnum.Rascunho, AgendamentoEnvioMensagemStatusEnum.Agendado].includes(item.statusId)
    }

    function podeMostrarBotaoDetalhesDoEnvio(item) {
      return item.statusId === AgendamentoEnvioMensagemStatusEnum.Processado
    }

    function convertToLocaleDateTimeString(datetime) {
      if (!datetime) return null

      const localeDate = new Date(datetime).toLocaleDateString('pt-BR')
      const localeTime = new Date(datetime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

      return `${localeDate} - ${localeTime}`
    }

    function getIconFromCanalTipoId(canalTipoId) {
      if (canalTipoId === TiposDeCanaisEnum.SMS) {
        return mdiMessageProcessing
      }

      if (canalTipoId === TiposDeCanaisEnum.Email) {
        return mdiEmail
      }

      if (canalTipoId === TiposDeCanaisEnum.WhatsApp) {
        return mdiWhatsapp
      }

      return null
    }

    return {
      headers,
      isEditarItemDialogOpen,
      agendamentoEmMassaSelecionado,
      openEditarItemDialog,
      openItemDetalhesDialog,
      podeMostrarBotaoEditar,
      podeMostrarBotaoDetalhesDoEnvio,
      functions: {
        convertToLocaleDateTimeString,
        getIconFromCanalTipoId,
      },
      icons: {
        mdiDotsVertical,
        mdiFileEdit,
        mdiClockOutline,
      },
    }
  },
}
</script>
